// Product analytics (PostHog) — the behavior/funnel layer that sits alongside
// the Meta Pixel (ad optimization) and Sentry (errors). This is what answers
// "what do real users actually DO?" — where they drop off in brand setup, how
// many reach Generate, which pages they visit.
//
// Everything here is GUARDED: if VITE_POSTHOG_KEY isn't set (or the lib fails
// to load / an ad blocker strips it) every fn silently no-ops. Analytics can
// never break the app, and the whole thing is INERT until a key is configured
// — so it can ship before the PostHog project even exists.
//
// Turn it on (2 steps):
//   1. Create a free PostHog project → copy the Project API Key (phc_...).
//   2. Add to frontend/.env (local) AND .env.production / GitHub secrets (prod):
//        VITE_POSTHOG_KEY=phc_xxxxx
//        VITE_POSTHOG_HOST=https://us.i.posthog.com   (optional; US is default,
//                                                       use eu.i.posthog.com for EU)
//   3. Rebuild — Vite bakes env at build time, same as Sentry/Pixel.
//
// The event that matters most is `brand_setup_step` (fired on EVERY wizard
// step). In PostHog: New insight → Funnel → add `brand_setup_step` filtered by
// its `step` property in wizard order → that graph shows exactly where cold
// traffic abandons onboarding.

import posthog from 'posthog-js';

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  'https://us.i.posthog.com';

let ready = false;

/** Init PostHog once, early (called from main.tsx before React renders).
 *  No-ops without a key so local dev + un-configured prod builds are
 *  completely unaffected. */
export function initAnalytics(): void {
  if (ready || typeof window === 'undefined') return;
  if (!KEY) {
    if (import.meta.env.PROD) {
      // Prod build with no key = we forgot the GitHub Action secret. Surface
      // it (same intent as Sentry's warning) so it doesn't silently miss.
      console.warn('[ANALYTICS] VITE_POSTHOG_KEY not set — product analytics disabled');
    }
    return;
  }
  try {
    posthog.init(KEY, {
      api_host: HOST,
      // Auto-capture pageviews on initial load + SPA route changes, and
      // pageleave for time-on-page. Custom funnel events (below) are what we
      // actually query, but per-route pageviews are useful exploratory data.
      capture_pageview: true,
      capture_pageleave: true,
      // Autocapture clicks/interactions too — traffic is tiny right now, so the
      // richer exploratory data is worth it (PostHog masks input VALUES by
      // default; passwords are always masked). Flip to false later if noisy.
      autocapture: true,
      persistence: 'localStorage+cookie',
    });
    ready = true;
    console.log(`[ANALYTICS] PostHog initialized (env=${import.meta.env.MODE})`);
  } catch {
    // never throw into the app
  }
}

/** Fire a product event. Safe to call anywhere — no-ops when uninitialized. */
export function track(event: string, props?: Record<string, unknown>): void {
  try {
    if (!ready) return;
    posthog.capture(event, props);
  } catch {
    /* analytics must never throw */
  }
}

/** Tie subsequent events to a signed-in user (called from useAuth on sign-in). */
export function identifyUser(id: string, props?: Record<string, unknown>): void {
  try {
    if (!ready) return;
    posthog.identify(id, props);
  } catch {
    /* noop */
  }
}

/** Clear identity on sign-out so the next visitor is a fresh anonymous id. */
export function resetAnalytics(): void {
  try {
    if (!ready) return;
    posthog.reset();
  } catch {
    /* noop */
  }
}
